import { type ReactNode, useEffect } from 'react';

import { retrySessionRestore } from '@shared/api';
import { clearChunkReloadGuard } from '@shared/lib/chunk-reload';
import { BOOT_STATUS, selectBootStatus, useSession } from '@shared/session';
import { StatusScreen } from '@shared/ui/status-screen';

import { APP_TEXT } from '../config/app.text';
import { RouteLoading } from '../layouts/route-loading.ui';

/**
 * 부팅 게이트. 인증 여부를 모르는 채로 라우트를 그리지 않는다.
 * - booting     : 로그인 · NotFound 를 포함해 어떤 라우트도 그리지 않는다
 * - unreachable : "연결할 수 없음 · 다시 시도". 로그인 화면으로 보내지 않는다
 *                 (사용자는 로그아웃된 줄 안다)
 * - maintenance : 점검 화면
 * - guest · authed : 라우터를 그린다
 */
export function BootGate({ children }: { children: ReactNode }) {
  const status = useSession(selectBootStatus);
  const settled = status === BOOT_STATUS.GUEST || status === BOOT_STATUS.AUTHED;

  useEffect(() => {
    // 부팅이 끝났다. 청크 복구의 "새로고침 1회" 표시를 지운다(다음 배포 때 다시 동작하게).
    if (settled) clearChunkReloadGuard();
  }, [settled]);

  if (status === BOOT_STATUS.BOOTING) {
    return <RouteLoading />;
  }

  if (status === BOOT_STATUS.UNREACHABLE) {
    return (
      <StatusScreen
        title={APP_TEXT.unreachableTitle}
        description={APP_TEXT.unreachableDescription}
        action={{ label: APP_TEXT.retry, onClick: () => void retrySessionRestore() }}
      />
    );
  }

  if (status === BOOT_STATUS.MAINTENANCE) {
    return (
      <StatusScreen
        title={APP_TEXT.maintenanceTitle}
        description={APP_TEXT.maintenanceDescription}
        action={{ label: APP_TEXT.retry, onClick: () => void retrySessionRestore() }}
      />
    );
  }

  return children;
}
